import "./App.css";
import React, { useState, useEffect, Suspense, lazy, useCallback, useRef } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
// import Footer from "./Components/Footer";
import Header from "./Components/Header";
import Products from "./Components/ClientFolder/Products";
import CreateProduct from "./Components/ClientFolder/CreateProduct";
import BulkProducts from "./Components/ClientFolder/BulkProducts";
import Coupons from "./Components/ClientFolder/Coupons";
import CreateCoupon from "./Components/ClientFolder/CreateCoupon";
import ProductOverview from "./Components/ClientFolder/ProductOverview";
import RatingsAndReviews from "./Components/ClientFolder/RatingsAndReviews";
import OrderOverview from "./Components/ClientFolder/OrderOverview";
import HomeCustomizations from "./Components/ClientFolder/HomeCustomizations";
import BannerCustomization from "./Components/BannerCustomization";
import Domains from "./Components/Domains";
import AdminPermissions from "./Components/AdminPermissions";
import InventoryManagement from "./Components/InventoryManagement";
import InventoryApprovals from "./Components/InventoryApprovals";
import InventoryLogs from "./Components/InventoryLogs";
import DynamicFooter from "./Components/DynamicFooter";
import Customers from "./Components/Customers";
import Dashboard from "./Components/Dashboard";
import CategoryManagement from "./Components/ClientFolder/CategoryManagement";
import ClientOrders from "./Components/ClientFolder/ClientOrders";
import { adminFetchJson } from "./services/apiService";
import OmniCommerceHub from "./Components/ClientFolder/OmniCommerce/OmniCommerceHub";
import GST from "./Components/GST";
import QRScanner from "./Components/QRScanner";
import MerchantBilling from "./Components/ClientFolder/MerchantBilling";
import WalkInAnalytics from "./Components/ClientFolder/WalkInAnalytics";
import PosCustomerAnalytics from "./Components/ClientFolder/PosCustomerAnalytics";
import PosOffers from "./Components/ClientFolder/PosOffers";
import TemplateManager from "./Components/ClientFolder/TemplateManager";
import Vendors from "./Components/Vendors";
import PurchaseOrders from "./Components/PurchaseOrders";
import CreatePurchaseOrder from "./Components/CreatePurchaseOrder";
import EditPurchaseOrder from "./Components/EditPurchaseOrder";
import MetricsManagement from "./Components/MetricsManagement";
import ZuntlyBrain from "./Components/ZuntlyBrain"; // AI Business Assistant
import MapProductVariants from "./Components/ClientFolder/MapProductVariants"; // Product Variant Mapping
import MapProductAddons from "./Components/ClientFolder/MapProductAddons"; // Product Add-On Mapping
import TimerDeals from "./Components/ClientFolder/TimerDeals";
import FreeCash from "./Components/ClientFolder/FreeCash";
import { getFreeCashAccess } from "./services/ClientService";
import FlashDeals from "./Components/ClientFolder/FlashDeals";
import ImageLinks from "./Components/ClientFolder/ImageLinks";
import PostOrderCommandCenter from "./Components/ClientFolder/PostOrderCommandCenter"; // Post-Order Command Center
import ReturnManagement from "./Components/ClientFolder/ReturnManagement"; // Return & Refund Management
import PostOrderSettings from "./Components/ClientFolder/PostOrderSettings"; // Post-Order Settings
import NotificationTemplates from "./Components/ClientFolder/NotificationTemplates"; // Push Notification Templates & Automation
import ManualNotifications from "./Components/ClientFolder/ManualNotifications"; // Manual Push Notifications
import ManualRating from "./Components/ClientFolder/ManualRating"; // Manual product star ratings (custom counts)
import ProductExpiry from "./Components/ClientFolder/ProductExpiry";
import DeliveryControls from "./Components/ClientFolder/DeliveryControls"; // Delivery App Controls & Driver Management
import ShiprocketSettings from "./Components/ClientFolder/ShiprocketSettings"; // Shiprocket Shipping Integration Settings
import CreateCustomOrder from "./Components/ClientFolder/CreateCustomOrder"; // Admin-initiated Custom Order
import ControlCentre from "./Components/ClientFolder/ControlCentre";
import SubscriptionGate from "./Components/Subscription/SubscriptionGate"; // Subscription paywall & renewal
import Profile from "./Components/Profile";

// Smart Notification System
import { OrderNotificationProvider } from "./context/OrderNotificationContext";
import OrderNotificationWidget from "./Components/Notifications/OrderNotificationWidget";

// Socket service for real-time notifications and session management
import socketService from "./services/socketService";

// Toast notifications (react-toastify)
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Login = lazy(() => import("./Components/Login"));
const CreateApp = lazy(() => import("./Components/ClientFolder/CreateApp"));
const Branches = lazy(() => import("./Components/Branches"));
const CreateBranch = lazy(() => import("./Components/CreateBranch"));
const ZuntlyGeoIQ = lazy(() => import("./Components/ZuntlyGeoIQ")); // AI Store Expansion Intelligence
const ZuntlyAI = lazy(() => import("./Components/ZuntlyAI")); // AI Merchant Revenue Intelligence
const ZuntlyCinematicStudio = lazy(() => import("./Components/ZuntlyCinematicStudio")); // AI Cinematic Film Studio
const MarketplaceSettings = lazy(() => import("./Components/ClientFolder/MarketplaceSettings")); // Zuntly Marketplace (B2B) — Super Admin

// PrivateRoute Component to protect routes
const PrivateRoute = ({ isLoggedIn, children }) => {
  if (!isLoggedIn) {
    localStorage.removeItem("userData"); // Clear localStorage if not logged in
    return <Navigate to="/login" />;
  }
  return children;
};

// RoleBasedRoute Component to protect routes based on roles
const RoleBasedRoute = ({ isLoggedIn, allowedRoles, allowRestrictedAccess = false, children }) => {
  if (!isLoggedIn) {
    localStorage.removeItem("userData"); // Clear localStorage if not logged in
    return <Navigate to="/login" />;
  }
  const userData = JSON.parse(localStorage.getItem("userData"));
  if (!userData || !allowedRoles.includes(userData.role)) {
    return <Navigate to="/dashboard" />; // Redirect unauthorized users
  }
  // Additional check for support admin access on role 2 users
  // Skip this check if allowRestrictedAccess is true (e.g., for inventory page)
  if (!allowRestrictedAccess && (allowedRoles.includes(1) || allowedRoles.includes(2))) { // Apply restriction only for routes where role 1 or 2 is allowed
    if (userData.role === 2 && !userData.supportAdminAccess) {
      return <Navigate to="/dashboard" />; // Redirect role 2 users without supportAdminAccess
    }
  }
  return children;
};

const FreeCashEnabledRoute = ({ isLoggedIn, children }) => {
  const [accessState, setAccessState] = React.useState("loading");

  React.useEffect(() => {
    let cancelled = false;
    const checkAccess = async () => {
      if (!isLoggedIn) {
        setAccessState("denied");
        return;
      }
      try {
        const res = await getFreeCashAccess();
        if (!cancelled) setAccessState(res?.enabled ? "allowed" : "denied");
      } catch {
        if (!cancelled) setAccessState("denied");
      }
    };
    checkAccess();
    return () => { cancelled = true; };
  }, [isLoggedIn]);

  if (accessState === "loading") {
    return <div className="p-8 text-center text-gray-500">Loading...</div>;
  }
  if (accessState === "denied") {
    return <Navigate to="/products" replace />;
  }
  return (
    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1, 2]}>
      {children}
    </RoleBasedRoute>
  );
};

// MasterWarehouseRoute Component to protect routes for master/warehouse users only
const MasterWarehouseRoute = ({ isLoggedIn, children }) => {
  const [isChecking, setIsChecking] = React.useState(true);
  const [hasAccess, setHasAccess] = React.useState(false);

  React.useEffect(() => {
    const checkMasterWarehouseAccess = async () => {
      if (!isLoggedIn) {
        setIsChecking(false);
        return;
      }

      try {
        const userData = JSON.parse(localStorage.getItem("userData"));
        if (!userData) {
          setHasAccess(false);
          setIsChecking(false);
          return;
        }

        // Role 1 (SuperAdmin) always has access
        if (userData.role === 1) {
          setHasAccess(true);
          setIsChecking(false);
          return;
        }

        // Role 2 (Admin) needs supportAdminAccess
        if (userData.role === 2) {
          const hasAccess = userData.supportAdminAccess === true;
          setHasAccess(hasAccess);
          setIsChecking(false);
          return;
        }

        // Other roles don't have access
        setHasAccess(false);
      } catch (error) {
        console.error("Error checking Purchase Control access:", error);
        setHasAccess(false);
      } finally {
        setIsChecking(false);
      }
    };

    checkMasterWarehouseAccess();
  }, [isLoggedIn]);

  if (isChecking) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return <Navigate to="/login" />;
  }

  if (!hasAccess) {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

// POSRoute Component to protect POS-specific routes
const POSRoute = ({ isLoggedIn, children }) => {
  const [isChecking, setIsChecking] = React.useState(true);
  const [hasAccess, setHasAccess] = React.useState(false);

  React.useEffect(() => {
    const checkPOSAccess = async () => {
      if (!isLoggedIn) {
        setIsChecking(false);
        return;
      }

      try {
        const userData = JSON.parse(localStorage.getItem("userData"));
        if (!userData) {
          setHasAccess(false);
          setIsChecking(false);
          return;
        }

        // Fetch branch data to check enablePOS status
        const { data } = await adminFetchJson('/clients/getBranchNamesForUser', {
          method: 'POST',
          body: {},
        });
        if (data.success && data.data) {
          let selectedBranch;
          
          if (userData.role === 1) {
            // For role 1 (super admin): selectedBranch in userData is the masterId
            // The API returns branches where masterId matches the selected branch
            selectedBranch = data.data.find(b => b.masterId === userData.selectedBranch);
          } else if (userData.role === 2) {
            // For role 2 (admin): use their own _id
            selectedBranch = data.data.find(b => b._id === userData._id);
          }
          
          if (selectedBranch && selectedBranch.enablePOS === true) {
            setHasAccess(true);
          } else {
            setHasAccess(false);
          }
        } else {
          setHasAccess(false);
        }
      } catch (error) {
        console.error("Error checking POS access:", error);
        setHasAccess(false);
      } finally {
        setIsChecking(false);
      }
    };

    checkPOSAccess();
  }, [isLoggedIn]);

  if (!isLoggedIn) {
    localStorage.removeItem("userData");
    return <Navigate to="/login" />;
  }

  if (isChecking) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!hasAccess) {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

/** Customer Analytics + POS Offers: Admin (role 2 + supportAdminAccess + masterAccount) or Support Admin (role 2 + supportAdminAccess). Super Admin (role 1) allowed. */
const PosLoyaltyAdminRoute = ({ isLoggedIn, children }) => {
  if (!isLoggedIn) {
    localStorage.removeItem("userData");
    return <Navigate to="/login" />;
  }

  try {
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");
    const role = userData?.role;
    const supportAdminAccess = userData?.supportAdminAccess === true;
    const allowed = role === 1 || (role === 2 && supportAdminAccess);
    if (!allowed) {
      return <Navigate to="/dashboard" />;
    }
  } catch {
    return <Navigate to="/dashboard" />;
  }

  return (
    <POSRoute isLoggedIn={isLoggedIn}>
      {children}
    </POSRoute>
  );
};

const App = () => {
  // Manage login state
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    return localStorage.getItem("userData") ? true : false;
  });

  // Move isSidebarExpanded state here
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);

  // State to determine if branch data exists.
  const [hasBranches, setHasBranches] = useState(true);
  
  // Track if force logout modal should be shown
  const [showForceLogoutModal, setShowForceLogoutModal] = useState(false);
  const [forceLogoutMessage, setForceLogoutMessage] = useState('');
  const [forceLogoutTitle, setForceLogoutTitle] = useState('');
  const forceLogoutHandled = useRef(false);

  // Handle force logout from another device or branch deactivation
  const handleForceLogout = useCallback((data) => {
    if (forceLogoutHandled.current) return;
    forceLogoutHandled.current = true;
    
    console.log('[App] Force logout triggered:', data);

    const isBranchInactive = data.errorType === 'branch_inactive';
    
    if (isBranchInactive) {
      setForceLogoutTitle('Branch Deactivated');
      setForceLogoutMessage('Branch is not active. Please contact Admin.');
    } else {
      setForceLogoutTitle('Session Expired');
      setForceLogoutMessage('You have been logged out because you logged in on another device. Only 2 simultaneous sessions are allowed.');
    }

    setShowForceLogoutModal(true);
    
    // Show toast notification
    toast.warning(isBranchInactive 
      ? 'Branch is not active. Please contact Admin.' 
      : 'You have been logged out because you logged in on another device.', {
      position: 'top-center',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
    
    // Clear local storage and reset state after a short delay
    setTimeout(() => {
      localStorage.clear();
      setIsLoggedIn(false);
      setShowForceLogoutModal(false);
      forceLogoutHandled.current = false;
      window.location.href = '/login';
    }, 2000);
  }, []);

  // Initialize socket connection and force logout listener when user is logged in
  useEffect(() => {
    if (isLoggedIn) {
      const userData = JSON.parse(localStorage.getItem("userData"));
      const sessionId = localStorage.getItem("sessionId");
      
      if (userData && sessionId) {
        // Set up force logout callback
        socketService.setForceLogoutCallback(handleForceLogout);
        
        // Connect to socket with session info
        socketService.connect(userData, sessionId);
      }
    }
    
    return () => {
      // Cleanup on unmount
      socketService.setForceLogoutCallback(null);
    };
  }, [isLoggedIn, handleForceLogout]);

  useEffect(() => {
    // Simulate fetching branch data from DB.
    // Replace this with your actual API call.
    const fetchBranchData = async () => {
      // For demonstration, assume no branch exists:
      const branchData = []; // e.g., await fetch(...).then(res => res.json());
      setHasBranches(branchData.length > 0);
    };

    fetchBranchData();
  }, []);

  useEffect(() => {
    if (!isLoggedIn) {
      localStorage.removeItem("userData");
    }
  }, [isLoggedIn]);

  // Handle login
  const handleLogin = (userData) => {
    // Set userData in localStorage when login is successful
    setIsLoggedIn(true);
    localStorage.setItem("userData", JSON.stringify(userData)); // Store userData in localStorage
  };

  // Retrieve current user data from localStorage.
  const currentUser = JSON.parse(localStorage.getItem("userData"));
  console.log("currentUser", currentUser);

  const isSuperAdminNoBranch =
    currentUser && currentUser.role === 1 && !currentUser.selectedBranch && !hasBranches && isLoggedIn;

  return (
    <main className={`
      transition-all duration-300
      ${isLoggedIn ? `
        // no margin on small screens
        pl-0 
        // when collapsed sidebar:
        md:pl-20 
        // when expanded sidebar:
        ${isSidebarExpanded ? 'md:ml-64' : ''}
        max-md:mt-[68px]
      ` : ''}
      w-full
    `}>
      {/* Force Logout Modal */}
      {showForceLogoutModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md mx-4 text-center">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">{forceLogoutTitle || 'Session Expired'}</h3>
            <p className="text-gray-600 mb-4">
              {forceLogoutMessage || 'You have been logged out. Please login again.'}
            </p>
            <p className="text-sm text-gray-500">Redirecting to login...</p>
          </div>
        </div>
      )}
      
      <Router>
        {isLoggedIn && (
          <OrderNotificationProvider>
            <Header onSidebarHover={(expanded) => setIsSidebarExpanded(expanded)} />
            <OrderNotificationWidget />
            {/* Subscription reminder banner / expired pay-wall (master & branch users) */}
            <SubscriptionGate />
            {/* Toast container for global toasts */}
            <ToastContainer position="top-right" autoClose={4000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
          </OrderNotificationProvider>
        )}
        <Suspense fallback={<div className="fixed inset-0 flex items-center justify-center bg-white/80 z-50">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[--primaryDarkShade1]"></div>
        </div>}>
          <Routes>
            {/* Public Route - Login */}
            <Route path="/" element={<Navigate to={isLoggedIn ? "/dashboard" : "/login"} />} />
            <Route path="/login" element={<Login onLogin={handleLogin} />} />

            {/* If Super Admin and no branch exists, register only allowed routes */}
            {isSuperAdminNoBranch ? (
              <>
                <Route
                  path="/domains"
                  element={
                    <PrivateRoute isLoggedIn={isLoggedIn}>
                      <Domains />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/branches"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1]}>
                      <Branches />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/createMasterBranch"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1]}>
                      <CreateBranch />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/createBranch"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1]}>
                      <CreateBranch />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/createBranch/:id"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1]}>
                      <CreateBranch />
                    </RoleBasedRoute>
                  }
                />
                {/* Any other URL manually entered will fall back to Domains */}
                <Route path="*" element={<Navigate to="/domains" />} />
              </>
            ) : (
              // Otherwise, load all routes normally.
              <>
                <Route
                  path="/products"
                  element={
                    <PrivateRoute isLoggedIn={isLoggedIn}>
                      <Products />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/createProduct"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1, 2]}>
                      <CreateProduct />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/bulk-products"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1, 2]}>
                      <BulkProducts />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/createApp"
                  element={
                    <PrivateRoute isLoggedIn={isLoggedIn}>
                      <CreateApp />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/gst"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1, 2]}>
                      <GST />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/branches"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1, 2]}>
                      <Branches />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/marketplace-settings"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1]}>
                      <MarketplaceSettings />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/createMasterBranch"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1]}>
                      <CreateBranch />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/createBranch"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1, 2]}>
                      <CreateBranch />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/createBranch/:id"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1, 2]}>
                      <CreateBranch />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/coupons"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1, 2]}>
                      <Coupons />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/createCoupon"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1, 2]}>
                      <CreateCoupon />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/editCoupon/:_id"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1, 2]}>
                      <CreateCoupon />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/productOverview/:id"
                  element={
                    <PrivateRoute isLoggedIn={isLoggedIn}>
                      <ProductOverview />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/editProduct"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1, 2]} >
                      <CreateProduct />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/orders"
                  element={
                    <PrivateRoute isLoggedIn={isLoggedIn}>
                      <ClientOrders />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/omni-commerce"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1]}>
                      <OmniCommerceHub />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/omni-commerce/orders"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1]}>
                      <OmniCommerceHub />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/omni-commerce/returns"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1]}>
                      <OmniCommerceHub />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/omni-commerce/cancellations"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1]}>
                      <OmniCommerceHub />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/omni-commerce/shipping"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1]}>
                      <OmniCommerceHub />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/omni-commerce/settings"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1]}>
                      <OmniCommerceHub />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/omni-commerce/sync-logs"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1]}>
                      <OmniCommerceHub />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/omni-commerce/analytics"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1]}>
                      <OmniCommerceHub />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/omni-commerce/audit-logs"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1]}>
                      <OmniCommerceHub />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/create-custom-order"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1, 2]}>
                      <CreateCustomOrder />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/orderOverview/:id"
                  element={
                    <PrivateRoute isLoggedIn={isLoggedIn}>
                      <OrderOverview />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/ratingsAndReviews"
                  element={
                    <PrivateRoute isLoggedIn={isLoggedIn}>
                      <RatingsAndReviews />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/post-order"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1, 2]}>
                      <PostOrderCommandCenter />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/return-management"
                  element={
                    <PrivateRoute isLoggedIn={isLoggedIn}>
                      <ReturnManagement />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/post-order-settings"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1, 2]}>
                      <PostOrderSettings />
                    </RoleBasedRoute>
                  }
                />

                {/* Push Notification System Routes */}
                <Route
                  path="/notification-templates"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1, 2]}>
                      <NotificationTemplates />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/manual-notifications"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1, 2]}>
                      <ManualNotifications />
                    </RoleBasedRoute>
                  }
                />

                <Route
                  path="/manual-rating"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1, 2]}>
                      <ManualRating />
                    </RoleBasedRoute>
                  }
                />

                <Route
                  path="/product-expiry"
                  element={
                    <PrivateRoute isLoggedIn={isLoggedIn}>
                      <ProductExpiry />
                    </PrivateRoute>
                  }
                />

                {/* Delivery App Controls */}
                <Route
                  path="/delivery-controls"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1, 2]}>
                      <DeliveryControls />
                    </RoleBasedRoute>
                  }
                />

                {/* Shiprocket Shipping Settings */}
                <Route
                  path="/shiprocket-settings"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1, 2]}>
                      <ShiprocketSettings />
                    </RoleBasedRoute>
                  }
                />

                <Route
                  path="/orderOverview"
                  element={
                    <PrivateRoute isLoggedIn={isLoggedIn}>
                      <OrderOverview />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/homeCustomizations"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1, 2]}>
                      <HomeCustomizations />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/domains"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1]}>
                      <Domains />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/adminPermissions"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1]}>
                      <AdminPermissions />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/banners"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1, 2]}>
                      <BannerCustomization />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/image-links"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1, 2]}>
                      <ImageLinks />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/inventory"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1, 2]} allowRestrictedAccess={true}>
                      <InventoryManagement />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/inventory-approvals"
                  element={
                    // <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1, 2]}>
                      <InventoryApprovals />
                    // </RoleBasedRoute>
                  }
                />
                <Route
                  path="/inventory-logs"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1, 2]}>
                      <InventoryLogs />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/dynamicFooter"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1, 2]}>
                      <DynamicFooter />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/customers"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1, 2]}>
                      <Customers />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="/dashboard"
                  element={
                    <PrivateRoute isLoggedIn={isLoggedIn} allowedRoles={[1, 2]}>
                      <Dashboard />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <PrivateRoute isLoggedIn={isLoggedIn}>
                      <Profile />
                    </PrivateRoute>
                  }
                />

                {/* Zuntly Brain - AI Command Console */}
                <Route
                  path="/zuntly-brain"
                  element={
                    <PrivateRoute isLoggedIn={isLoggedIn} allowedRoles={[1, 2]}>
                      <ZuntlyBrain />
                    </PrivateRoute>
                  }
                />

                {/* Zuntly GeoIQ - AI Store Expansion Intelligence */}
                <Route
                  path="/zuntly-geoiq"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1]}>
                      <ZuntlyGeoIQ />
                    </RoleBasedRoute>
                  }
                />

                {/* Zuntly AI - Merchant Revenue Intelligence */}
                <Route
                  path="/zuntly-ai"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1]}>
                      <ZuntlyAI />
                    </RoleBasedRoute>
                  }
                />

                {/* Zuntly AI Cinematic Studio */}
                <Route
                  path="/zuntly-cinematic-studio"
                  element={
                    <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1]}>
                      <ZuntlyCinematicStudio />
                    </RoleBasedRoute>
                  }
                />

                <Route
                  path="/control-centre"
                  element={
                    <PrivateRoute isLoggedIn={isLoggedIn} allowedRoles={[1, 2]}>
                      <ControlCentre />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/categories"
                  element={
                    <PrivateRoute isLoggedIn={isLoggedIn} allowedRoles={[1, 2]}>
                      <CategoryManagement />
                    </PrivateRoute>
                  }
                />
                <Route path="/qr-scanner" element={
                  <PrivateRoute isLoggedIn={isLoggedIn}>
                    <QRScanner />
                  </PrivateRoute>
                } />
                <Route path="/merchant-billing" element={
                  <POSRoute isLoggedIn={isLoggedIn}>
                    <MerchantBilling />
                  </POSRoute>
                } />
                <Route path="/walk-in-analytics" element={
                  <POSRoute isLoggedIn={isLoggedIn}>
                    <WalkInAnalytics />
                  </POSRoute>
                } />
                <Route path="/template-manager/customer-analytics" element={
                  <PosLoyaltyAdminRoute isLoggedIn={isLoggedIn}>
                    <PosCustomerAnalytics />
                  </PosLoyaltyAdminRoute>
                } />
                <Route path="/template-manager/pos-offers" element={
                  <PosLoyaltyAdminRoute isLoggedIn={isLoggedIn}>
                    <PosOffers />
                  </PosLoyaltyAdminRoute>
                } />
                <Route path="/template-manager" element={
                  <POSRoute isLoggedIn={isLoggedIn}>
                    <TemplateManager />
                  </POSRoute>
                } />
                
                {/* Purchase Order & Vendor Management Routes */}
                <Route path="/vendors" element={
                  <MasterWarehouseRoute isLoggedIn={isLoggedIn}>
                    <Vendors />
                  </MasterWarehouseRoute>
                } />
                <Route path="/purchase-orders" element={
                  <MasterWarehouseRoute isLoggedIn={isLoggedIn}>
                    <PurchaseOrders />
                  </MasterWarehouseRoute>
                } />
                <Route path="/create-purchase-order" element={
                  <MasterWarehouseRoute isLoggedIn={isLoggedIn}>
                    <CreatePurchaseOrder />
                  </MasterWarehouseRoute>
                } />
                <Route path="/edit-purchase-order/:poId" element={
                  <MasterWarehouseRoute isLoggedIn={isLoggedIn}>
                    <EditPurchaseOrder />
                  </MasterWarehouseRoute>
                } />
                <Route path="/metrics" element={
                  <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1, 2]}>
                    <MetricsManagement />
                  </RoleBasedRoute>
                } />
                
                {/* Product Variant Mapping Route */}
                <Route path="/product-variants" element={
                  <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1, 2]}>
                    <MapProductVariants />
                  </RoleBasedRoute>
                } />

                <Route path="/product-addons" element={
                  <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1, 2]}>
                    <MapProductAddons />
                  </RoleBasedRoute>
                } />

                <Route path="/timer-deals" element={
                  <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1, 2]}>
                    <TimerDeals />
                  </RoleBasedRoute>
                } />

                <Route path="/flash-deals" element={
                  <RoleBasedRoute isLoggedIn={isLoggedIn} allowedRoles={[1, 2]}>
                    <FlashDeals />
                  </RoleBasedRoute>
                } />

                <Route path="/free-cash" element={
                  <FreeCashEnabledRoute isLoggedIn={isLoggedIn}>
                    <FreeCash />
                  </FreeCashEnabledRoute>
                } />
                
                {/* Default fallback route */}
                <Route
                  path="*"
                  element={<Navigate to={isLoggedIn ? "/products" : "/login"} />}
                />
              </>
            )}
          </Routes>
        </Suspense>
      </Router>
    </main>
  );
};

export default App;
